import { useEffect, useRef, useState } from 'react';
import type {
  AspectRatio,
  GenerationProgress,
  GenerationResult,
} from '../generation/types';
import { formatSeconds } from '../lib/format';
import { IconAlert, IconDownload, IconRefresh, IconSpark } from './Icons';
import { LatentField } from './LatentField';

interface StageProps {
  progress: GenerationProgress;
  result: GenerationResult | null;
  /** The prompt the current result was made from. */
  resultPrompt: string;
  error: string | null;
  /** Drives the frame shape while nothing has been rendered yet. */
  aspectRatio: AspectRatio;
  onDownload: () => void;
  onAgain: () => void;
  canAgain: boolean;
}

const STEPS = [
  { key: 'queued', label: 'Queued' },
  { key: 'generating', label: 'Generating' },
  { key: 'finalizing', label: 'Encoding' },
  { key: 'done', label: 'Done' },
] as const;

const stageIndex = (stage: GenerationProgress['stage']): number => {
  switch (stage) {
    case 'queued':
      return 0;
    case 'generating':
      return 1;
    case 'finalizing':
      return 2;
    case 'done':
      return 3;
    default:
      return -1;
  }
};

/**
 * Turns whatever internal phase text the engine reports (model filenames,
 * sampler stage names, etc.) into plain, model-agnostic status copy. The
 * underlying engine is swappable — the words on screen shouldn't change
 * depending on which one happens to be running.
 */
function humanizePhase(raw: string): string {
  const s = raw.toLowerCase();
  if (!s.trim()) return 'Working';
  if (s.includes('loading model') || s.includes('downloading')) return 'Preparing engine';
  if (s.includes('upload')) return 'Uploading image';
  if (s.includes('encoding prompt') || s.includes('reading')) return 'Reading prompt';
  if (s.includes('denois') || s.includes('sampl')) return 'Generating';
  if (s.includes('vae') || s.includes('decod') || s.includes('fetching file') || s.includes('encod'))
    return 'Finishing';
  if (s.includes('queue') || s.includes('reserving')) return 'Queued';
  if (s.includes('prepar')) return 'Preparing';
  return 'Generating';
}

interface LiveProgress {
  /** 0..1 — always the real value from the engine, never guessed. */
  progress: number;
  /** Seconds left, ticking down once a second between real updates. */
  etaSeconds: number | null;
}

const RUNNING_STAGES = new Set<GenerationProgress['stage']>([
  'queued',
  'generating',
  'finalizing',
]);

/**
 * The percentage is always the real, current value the engine reported —
 * never estimated or extrapolated, so it can never show something that
 * turns out to be wrong. (An earlier version tried to smooth it by guessing
 * forward between polls; that guess could run ahead of a real update and
 * then visibly snap backward, which is worse than a plain number that just
 * updates every ~700ms — a rate a person reads as "live" anyway.)
 *
 * The ETA is the one number worth actively ticking down client-side: unlike
 * progress, a countdown re-adjusting between real updates is exactly what a
 * countdown is supposed to do, so there's no "is this a lie?" risk there.
 */
function useLiveProgress(progress: GenerationProgress): LiveProgress {
  const [etaDisplay, setEtaDisplay] = useState<number | null>(progress.etaSeconds);
  const anchor = useRef({ eta: progress.etaSeconds, at: performance.now() });

  useEffect(() => {
    anchor.current = { eta: progress.etaSeconds, at: performance.now() };
    setEtaDisplay(progress.etaSeconds);
  }, [progress.etaSeconds]);

  useEffect(() => {
    if (!RUNNING_STAGES.has(progress.stage)) return;
    const id = window.setInterval(() => {
      const a = anchor.current;
      if (a.eta === null) return;
      const elapsedS = (performance.now() - a.at) / 1000;
      setEtaDisplay(Math.max(0, a.eta - elapsedS));
    }, 1000);
    return () => window.clearInterval(id);
  }, [progress.stage]);

  return { progress: progress.progress, etaSeconds: etaDisplay };
}

export function Stage({
  progress,
  result,
  resultPrompt,
  error,
  aspectRatio,
  onDownload,
  onAgain,
  canAgain,
}: StageProps) {
  const isRunning =
    progress.stage === 'queued' ||
    progress.stage === 'generating' ||
    progress.stage === 'finalizing';
  const isDone = progress.stage === 'done' && !!result;
  const current = stageIndex(progress.stage);
  const live = useLiveProgress(progress);
  const statusText = humanizePhase(progress.message);

  // Once a clip exists, the frame follows the CLIP's shape, not the current
  // setting — otherwise changing the toggle would distort an existing result.
  const portrait =
    isDone && result && result.width && result.height
      ? result.height > result.width
      : aspectRatio === 'portrait';

  return (
    <div className="stage-frame">
      <div className={`stage-surface ${portrait ? 'is-portrait' : ''}`}>
        {isDone && result ? (
          result.isStillFallback ? (
            <img src={result.videoUrl} alt={`Generated still: ${resultPrompt}`} />
          ) : (
            <video
              key={result.videoUrl}
              src={result.videoUrl}
              poster={result.posterUrl || undefined}
              controls
              autoPlay
              loop
              muted
              playsInline
              aria-label={`Generated video: ${resultPrompt}`}
            />
          )
        ) : (
          <>
            <LatentField
              progress={live.progress}
              active={isRunning}
              settled={progress.stage === 'done'}
            />
            <div className="stage-overlay">
              <span className="stage-tag">
                <IconSpark size={12} />
                {isRunning ? 'Latent field · active' : 'Latent field · idle'}
              </span>
              <div className="stage-center">
                {error ? (
                  <>
                    <span className="stage-headline" style={{ color: 'var(--accent-2)' }}>
                      Generation failed
                    </span>
                    <span className="stage-sub">{error}</span>
                  </>
                ) : isRunning ? (
                  <>
                    <span className="stage-headline">{statusText}</span>
                    <span className="stage-sub">
                      {live.etaSeconds !== null
                        ? `about ${formatSeconds(live.etaSeconds)} left`
                        : 'working…'}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="stage-headline">The canvas is empty</span>
                    <span className="stage-sub">write a prompt, then hit generate</span>
                  </>
                )}
              </div>
            </div>
          </>
        )}

        <span className="reg-mark tl" />
        <span className="reg-mark tr" />
        <span className="reg-mark bl" />
        <span className="reg-mark br" />
      </div>

      {/* ── Progress ──────────────────────────────────────────────────────── */}
      {(isRunning || progress.stage === 'done') && (
        <div className="progress-panel">
          <div className="progress-top">
            <span className="progress-message">
              {isRunning && <span className="engine-dot" style={{ color: 'var(--accent)' }} />}
              {statusText}
            </span>
            <span className="progress-readout">
              <span>
                <b>{Math.round(live.progress * 100)}</b>%
              </span>
              <span>
                left <b style={{ fontSize: 13 }}>{formatSeconds(live.etaSeconds)}</b>
              </span>
            </span>
          </div>

          <div
            className="progress-track"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(live.progress * 100)}
            aria-label="Generation progress"
          >
            <div className="progress-fill" style={{ width: `${live.progress * 100}%` }} />
          </div>

          <div className="step-rail">
            {STEPS.map((s, i) => (
              <div
                key={s.key}
                className={`step-cell ${i < current ? 'is-done' : ''} ${i === current ? 'is-active' : ''}`}
              >
                <span className="s-bar" />
                <span className="s-label">{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Result actions ────────────────────────────────────────────────── */}
      {isDone && result && (
        <div className="result-bar">
          <div className="result-meta">
            <span>
              <i>size</i>
              {result.width}×{result.height}
            </span>
            <span>
              <i>length</i>
              {result.durationSeconds.toFixed(1)}s
            </span>
            <span>
              <i>format</i>
              {result.mimeType.replace('video/', '').replace('image/', '').toUpperCase()}
            </span>
          </div>
          <div className="result-actions">
            <button type="button" className="btn btn-sm" onClick={onAgain} disabled={!canAgain}>
              <IconRefresh size={14} /> Again
            </button>
            <button type="button" className="btn btn-sm btn-primary" onClick={onDownload}>
              <IconDownload size={14} /> Download
            </button>
          </div>
        </div>
      )}

      {isDone && result?.isStillFallback && (
        <div style={{ padding: '0 20px 16px' }}>
          <p className="notice">
            <IconAlert size={15} />
            This browser cannot record video (no MediaRecorder), so the mock engine returned a
            still frame instead. Chrome, Edge and Firefox produce a real video file.
          </p>
        </div>
      )}
    </div>
  );
}
